import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { C, card, label, btn, input } from "../lib/theme";

/* Articles: public reading (published only — this page also serves logged-out
   visitors and crawlers), moderator writing. Editing happens inline: moderators
   see New/Edit/Publish/Delete controls on the same page. */

export default function Articles({ me }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isMod = !!me?.is_moderator;
  const [list, setList] = useState(null);
  const [article, setArticle] = useState(null);
  const [editing, setEditing] = useState(null);   // {id?, title, body, published}
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const loadList = () => (isMod ? api.modArticles() : api.articles())
    .then(setList).catch((e) => setErr(e.message));
  useEffect(() => { if (!id) { setArticle(null); loadList(); } }, [id, isMod]);
  useEffect(() => { if (id) api.article(id).then(setArticle)
    .catch((e) => setErr(e.message)); }, [id]);
  useEffect(() => {
    const prev = document.title;
    document.title = article ? `${article.title} | Astrology Calendar`
      : "Articles | Astrology Calendar";
    return () => { document.title = prev; };
  }, [article]);

  const save = () => {
    setBusy(true); setErr("");
    const req = editing.id
      ? api.modArticleUpdate(editing.id, { title: editing.title, body: editing.body,
          published: editing.published })
      : api.modArticleCreate({ title: editing.title, body: editing.body,
          published: editing.published });
    req.then(() => { setEditing(null); id ? api.article(id).then(setArticle) : loadList(); })
      .catch((e) => setErr(e.message)).finally(() => setBusy(false));
  };

  if (editing) return (
    <Shell standalone={!me}>
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={label}>{editing.id ? "EDIT ARTICLE" : "NEW ARTICLE"}</div>
        <input value={editing.title} placeholder="Title"
          onChange={(e) => setEditing({ ...editing, title: e.target.value })}
          style={{ ...input, width: "100%", boxSizing: "border-box", marginBottom: 8,
            fontSize: 16 }} />
        <textarea value={editing.body} rows={18}
          placeholder="Plain text. Blank lines separate paragraphs."
          onChange={(e) => setEditing({ ...editing, body: e.target.value })}
          style={{ ...input, width: "100%", boxSizing: "border-box",
            fontFamily: "inherit", lineHeight: 1.6 }} />
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5,
          margin: "10px 0", cursor: "pointer" }}>
          <input type="checkbox" checked={editing.published}
            onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
            style={{ accentColor: C.brass }} />
          Published (visible to everyone, including logged-out visitors)
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btn()} disabled={busy || !editing.title.trim() || !editing.body.trim()}
            onClick={save}>{busy ? "Saving…" : "Save"}</button>
          <button style={btn(false)} disabled={busy}
            onClick={() => setEditing(null)}>Cancel</button>
        </div>
        {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
      </div>
    </Shell>
  );

  if (id) return (
    <Shell standalone={!me}>
      <button onClick={() => nav("/articles")} style={{ all: "unset", cursor: "pointer",
        color: C.brass, fontSize: 12.5, marginBottom: 10 }}>← All articles</button>
      {!article ? <div style={{ color: C.faint }}>Loading…</div> : (
        <article style={{ ...card }}>
          <h1 style={{ fontFamily: C.displayFont, fontSize: 28, fontWeight: 500,
            margin: "0 0 4px" }}>{article.title}</h1>
          <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 14 }}>
            {article.author_name && <>{article.author_name} · </>}
            {String(article.created_at).slice(0, 10)}
            {String(article.updated_at).slice(0, 10) !== String(article.created_at).slice(0, 10)
              && <> · updated {String(article.updated_at).slice(0, 10)}</>}
          </div>
          {article.body.split("\n\n").map((p, i) => (
            <p key={i} style={{ fontSize: 14, lineHeight: 1.75, color: C.text,
              margin: "0 0 12px" }}>{p}</p>))}
          {isMod && (
            <div style={{ display: "flex", gap: 12, marginTop: 10,
              borderTop: `1px solid ${C.line}`, paddingTop: 10 }}>
              <button onClick={() => setEditing({ id: article.id, title: article.title,
                  body: article.body, published: true })}
                style={{ all: "unset", cursor: "pointer", color: C.brass, fontSize: 11.5 }}>
                edit</button>
              <button onClick={() => { if (confirm("Delete this article?"))
                  api.modArticleDelete(article.id).then(() => nav("/articles")); }}
                style={{ all: "unset", cursor: "pointer", color: C.rose, fontSize: 11.5 }}>
                delete</button>
            </div>)}
        </article>)}
      {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
    </Shell>
  );

  return (
    <Shell standalone={!me}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
        <h1 style={{ fontFamily: C.displayFont, fontSize: 28, fontWeight: 500, margin: 0 }}>
          Articles</h1>
        {isMod && <button style={{ ...btn(), marginLeft: "auto" }}
          onClick={() => setEditing({ title: "", body: "", published: false })}>
          New article</button>}
      </div>
      {list === null && <div style={{ color: C.faint }}>Loading…</div>}
      {list !== null && list.length === 0 && (
        <div style={{ ...card, fontSize: 13, color: C.dim }}>
          Nothing published yet — articles on Zodiacal Releasing, the research
          program, and findings will appear here.</div>)}
      {(list || []).map((a) => (
        <Link key={a.id} to={`/articles/${a.id}`}
          style={{ textDecoration: "none", color: "inherit" }}>
          <div style={{ ...card, marginBottom: 10, cursor: "pointer" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{a.title}</span>
              {isMod && !a.published && <span style={{ fontSize: 9, letterSpacing: "0.1em",
                color: C.rose, border: `1px solid ${C.rose}`, borderRadius: 3,
                padding: "1px 5px" }}>DRAFT</span>}
              <span style={{ marginLeft: "auto", fontSize: 10.5, color: C.faint }}>
                {String(a.created_at).slice(0, 10)}</span>
            </div>
            <div style={{ fontSize: 12.5, color: C.dim, marginTop: 4 }}>{a.excerpt}…</div>
          </div>
        </Link>))}
      {err && <div style={{ color: C.rose, fontSize: 12 }}>{err}</div>}
    </Shell>
  );
}

/* Logged-out visitors get a standalone page (no app chrome); logged-in users are
   already inside the app layout. */
function Shell({ standalone, children }) {
  if (!standalone) return <div style={{ maxWidth: 720 }}>{children}</div>;
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: C.font, padding: "30px 16px 60px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ fontFamily: C.displayFont, fontSize: 22, marginBottom: 14 }}>
          Astrology Calendar</div>
        {children}
      </div>
    </div>
  );
}
